<template>
  <li :class="[$style.item, {[$style.itemDigital]: type === 'digital',[$style.itemSuccessPageVariant]: successPageVariant }]">
    <div v-if="type === 'digital'" :class="$style.itemImage">
      <img
        :src="card.cardStyle.image"
        :alt="card.cardStyle.name"
        :class="$style.itamImageTag"
        loading="lazy"
      >
    </div>
    <div :class="$style.itemLeftBlock">
      <div :class="$style.itemName">
        {{ card.cardStyle?.name ? card.cardStyle.name : $awt('aw.pages.gift_card_request.cards.default') }}
      </div>
      <div :class="$style.itemValue">
        {{ $price({ gross: card.value }, DEFAULT_CURRENCY) }}
      </div>
    </div>
    <div v-if="type === 'digital'" :class="$style.itemCenterBlock">
      <div :class="$style.itemName">
        {{ card.name }}
      </div>
      <div :class="$style.itemName">
        {{ card.email }}
      </div>
    </div>
    <div :class="$style.itemQuantity">
      {{ $awt('aw.pages.gift_card_request.cards.quantity', { quantity: card.quantity }) }}
    </div>
    <lv-button
      v-if="!successPageVariant"
      type="button"
      styling="small-secondary-dark-just-icon"
      shrink
      :class="$style.itemDeleteButton"
      :title="$awt('aw.pages.gift_card_request.cards.delete')"
      :aria-label="$awt('aw.pages.gift_card_request.cards.delete')"
      @click="deleteCard"
    >
      <lv-icon name="trash-version2-16" :size="16" />
    </lv-button>
  </li>
</template>

<script setup>
  import { DEFAULT_CURRENCY } from '~~/common/config/LocaleConfig';
  import { LvButton, LvIcon } from '~~/common/components/loginet-vue-shop/index';

  const props = defineProps({
    card: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: (value) => ['physical', 'digital'].includes(value),
    },
    successPageVariant: {
      type: Boolean,
      default: false,
    },
  });
  const emits = defineEmits(['delete-card']);

  function deleteCard () {
    emits('delete-card', props.card);
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.item {
  display: grid;
  align-items: center;
  padding: 4px;
  border-radius: 12px;
  background: $color-background-2;
  grid-template-columns: auto 52px 32px;
  gap: 8px;
  grid-template-areas: "leftBlock quantity deleteButton";

  @include tablet(min) {
    padding: 8px 16px 8px 8px;
  }

  &Image {
    display: flex;
    align-items: center;
    width: 32px;
    height: 32px;
    margin: 4px 0;
    padding: 6px;
    border-radius: 8px;
    background: $color-background-4;
    grid-area: image;

    &Tag {
      border-radius: 2px;
    }

    @include tablet(min) {
      width: 40px;
      height: 40px;
      margin: 0;
      padding: 8px;
    }

  }

  &LeftBlock {
    grid-area: leftBlock;
    display: flex;
    flex-direction: column;
    width: 100%;
    border-right: 1px dashed $color-dash-separator;
    gap: 4px;
  }

  &CenterBlock {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    margin-top: -1px;
    border-top: 1px dashed $color-dash-separator;
    grid-area: centerBlock;
    gap: 4px;


    @include tablet(min) {
      flex-direction: column;
      margin-top: 0;
      border-top: none;
      border-right: 1px dashed $color-dash-separator;
    }
  }

  &Quantity,
  &Value {
    @include font($font-weight-bold-v2, 10px, 16px);
    @include tablet(min) {
      @include font(null, 12px, 16px);
    }
  }

  &Name {
    @include font($font-weight-normal-v2, 10px, 16px);
    max-width: 50%;
    word-break: break-word;
    hyphens: auto;

    @include tablet(min) {
      @include font(null, 12px, 16px);
    }

  }

  &Quantity {
    grid-area: quantity;
    text-align: right;
  }

  &DeleteButton {
    grid-area: deleteButton;
  }

  &Digital {
    grid-template-areas: "image quantity deleteButton" "leftBlock leftBlock leftBlock" "centerBlock centerBlock centerBlock";
    gap: 4px 8px;

    @include tablet(min) {
      grid-template-areas: "image leftBlock centerBlock quantity deleteButton";
      grid-template-columns: 40px 1fr 1fr  52px 32px;
      gap: 8px;
    }

    .itemLeftBlock {
      flex-direction: row;
      justify-content: space-between;
      width: 100%;
      border-right: none;

      @include tablet(min) {
        flex-direction: column;
        border-right: 1px dashed $color-dash-separator;
      }
    }
  }

  &SuccessPageVariant {
    grid-template-columns: auto 52px;
    grid-template-areas: "image quantity" "leftBlock leftBlock leftBlock" "centerBlock centerBlock centerBlock";

    @include tablet(min) {
      grid-template-areas: "image leftBlock centerBlock quantity";
      grid-template-columns: 40px 1fr 1fr  52px;
    }

    .itemName {
      @include multiline-ellipsis(1);
      max-width: 100%;
    }
  }
}

</style>
